/*
 * Copyright (c) 2023 Limpide
 *
 *  Permission is hereby granted, free of charge, to any person obtaining a copy of
 *  this software and associated documentation files (the "Software"), to deal in
 *  the Software without restriction, including without limitation the rights to
 *  use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of
 *  the Software, and to permit persons to whom the Software is furnished to do so,
 *  subject to the following conditions:
 *
 *  The above copyright notice and this permission notice shall be included in all
 *  copies or substantial portions of the Software.
 *
 *  THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 *  IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS
 *  FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
 *  COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER
 *  IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN
 *  CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

import Base from '@scripts/components/base'
import { Application, Graphics, ICanvas, LINE_CAP } from 'pixi.js'

export default class UnderlinedText extends Base {
    element: HTMLElement
    wrappers: NodeListOf<HTMLElement>
    lines: Array<Application<ICanvas>> = []

    constructor(element: HTMLElement) {
        super()

        this.element = element
        this.wrappers = element.querySelectorAll('span')
    }

    start() {
        this.drawCanvas()

        if (this.lines.length) {
            this.drawLines()
            this.addListeners()
        }
    }

    drawCanvas() {
        this.wrappers.forEach((wrapper: HTMLElement) => {
            const app = new Application({
                autoDensity: true,
                backgroundAlpha: 0,
                antialias: true,
                resolution: devicePixelRatio,
                resizeTo: wrapper
            })

            wrapper.appendChild(app.view as HTMLCanvasElement)
            this.lines.push(app)
        })
    }

    drawLines() {
        this.wrappers.forEach((wrapper: HTMLElement, index: number) => {
            const app = this.lines[index]
            const width = wrapper.offsetWidth
            const height = wrapper.offsetHeight
            const bezier = new Graphics()
            const bend = this.computeBend(width)
            const lineWidth = window.innerWidth < 1024 ? 2 : 5

            bezier.lineStyle({ width: lineWidth, color: this.element.dataset.color, cap: LINE_CAP.ROUND })
            bezier.bezierCurveTo(width / 3, bend, width / 2, bend, width - 10, -5)

            bezier.position.x = 5
            bezier.position.y = height - 5

            app.stage.removeChildren()
            app.stage.addChild(bezier)
        })
    }

    addListeners() {
        window.addEventListener('resize', () => {
            this.wrappers.forEach((wrapper: HTMLElement, index: number) => {
                const width = wrapper.offsetWidth
                const height = wrapper.offsetHeight
                const app = this.lines[index]

                app.renderer.resize(width, height)
                this.drawLines()
            })
        })
    }

    computeBend(value: number): number {
        const bend = 5
        const step = 500
        const number = (Math.round(value) * 100) / step

        return ((bend * number) / 100 + bend) * -1
    }
}
